/* ============================================================
   data.jsx — procedural "AI art" placeholders + mock gallery data
   Self-contained: no external image dependencies.
   ============================================================ */

/* deterministic PRNG */
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* curated painterly palettes */
const ART_PALETTES = {
  dusk:   { name: "Dusk",   base: "#1B1632", cols: ["#5B49FF", "#9B4DFF", "#FF5AA8", "#C9B8FF"] },
  sunset: { name: "Sunset", base: "#2A1410", cols: ["#FF7A59", "#FFB14E", "#FF5E7E", "#7A3CFF"] },
  ocean:  { name: "Ocean",  base: "#06243F", cols: ["#1FA2FF", "#12D8FA", "#36E2A0", "#7CF6E0"] },
  bloom:  { name: "Bloom",  base: "#2A0E2C", cols: ["#FF5AA8", "#C44DFF", "#7A6BFF", "#FFC2E0"] },
  forest: { name: "Forest", base: "#0C2A1A", cols: ["#2BB673", "#8CD867", "#1F8A5B", "#E7F5C9"] },
  ember:  { name: "Ember",  base: "#240A0A", cols: ["#FF4D4D", "#FF8A3D", "#FFB347", "#FFD08A"] },
  sand:   { name: "Sand",   base: "#2A1C10", cols: ["#E8C39E", "#C98A5E", "#9E7B4A", "#F5E6D3"] },
  mono:   { name: "Mono",   base: "#161519", cols: ["#3A3A44", "#6E6E78", "#A8A8B2", "#E4E4E8"] },
  arctic: { name: "Arctic", base: "#0E2230", cols: ["#5EC8E5", "#A7E8F2", "#3A7BD5", "#DCF3F8"] },
  candy:  { name: "Candy",  base: "#241237", cols: ["#7B5BFF", "#FF6FD8", "#5BE0FF", "#FFD66B"] },
};
const PALETTE_KEYS = Object.keys(ART_PALETTES);

/* build a layered radial-gradient background that reads like abstract art */
function artBackground(seed, paletteKey) {
  const pal = ART_PALETTES[paletteKey] || ART_PALETTES.dusk;
  const rnd = mulberry32(seed * 2654435761 >>> 0);
  const blobs = 4 + Math.floor(rnd() * 2);
  const layers = [];
  for (let i = 0; i < blobs; i++) {
    const col = pal.cols[Math.floor(rnd() * pal.cols.length)];
    const x = Math.round(rnd() * 100);
    const y = Math.round(rnd() * 100);
    const r = 30 + Math.round(rnd() * 45);
    const a = 0.55 + rnd() * 0.4;
    const shape = rnd() > 0.5 ? "circle" : `ellipse ${40 + Math.round(rnd()*50)}% ${40 + Math.round(rnd()*50)}%`;
    layers.push(`radial-gradient(${shape} at ${x}% ${y}%, ${hexA(col, a)} 0%, ${hexA(col, 0)} ${r}%)`);
  }
  // a soft directional wash on top of the base
  const ang = Math.round(rnd() * 360);
  layers.push(`linear-gradient(${ang}deg, ${hexA(pal.cols[0], .25)}, ${hexA(pal.base, .1)})`);
  return { backgroundColor: pal.base, backgroundImage: layers.join(", "), paletteName: pal.name };
}

function hexA(hex, a) {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a.toFixed(3)})`;
}

/* fine grain overlay (data-uri SVG noise) — gives the gradients a painterly tooth */
const GRAIN_URI =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

/* ---- ArtImage component ---- */
function ArtImage({ seed, palette, ratio = "1 / 1", radius = "var(--r-md)", style = {}, children, grain = true }) {
  const bg = React.useMemo(() => artBackground(seed, palette), [seed, palette]);
  return (
    <div
      className="art-img"
      style={{
        position: "relative",
        aspectRatio: ratio,
        borderRadius: radius,
        overflow: "hidden",
        backgroundColor: bg.backgroundColor,
        backgroundImage: bg.backgroundImage,
        ...style,
      }}
    >
      {grain && (
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: GRAIN_URI, backgroundSize: "140px 140px",
          mixBlendMode: "overlay", opacity: 0.5, pointerEvents: "none",
        }} />
      )}
      {/* subtle top sheen */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(180deg, rgba(255,255,255,.10), rgba(0,0,0,0) 30%, rgba(0,0,0,.12))",
      }} />
      {children}
    </div>
  );
}

/* ---- mock gallery data ---- */
const MODELS = ["Promzi X", "Promzi Flux", "Promzi Lite", "Aurora v3"];
const AUTHORS = [
  { name: "김도윤", nameEn: "Doyoon Kim", handle: "doyoon", tone: "#5B49FF" },
  { name: "이서연", nameEn: "Seoyeon Lee", handle: "seoyeon", tone: "#FF5AA8" },
  { name: "박지호", nameEn: "Jiho Park", handle: "jiho", tone: "#1FA2FF" },
  { name: "최유진", nameEn: "Yujin Choi", handle: "yujin", tone: "#2BB673" },
  { name: "정민재", nameEn: "Minjae Jung", handle: "minjae", tone: "#FF8A3D" },
  { name: "한소희", nameEn: "Sohee Han", handle: "sohee", tone: "#9B4DFF" },
];

const PROMPTS = [
  { t: "비 내리는 네온 사이버펑크 골목, 젖은 아스팔트의 반사광", en: "Neon cyberpunk alley in the rain, reflections on wet asphalt", tags: ["사이버펑크", "네온", "도시"], r: "3 / 4" },
  { t: "미니멀 파스텔 추상 구성, 부드러운 그라데이션과 기하 도형", en: "Minimal pastel abstract composition, soft gradients and geometric shapes", tags: ["추상", "미니멀", "파스텔"], r: "1 / 1" },
  { t: "안개 낀 산맥 위로 떠오르는 보랏빛 새벽, 시네마틱 와이드", en: "Purple dawn rising over misty mountains, cinematic wide shot", tags: ["풍경", "시네마틱"], r: "4 / 3" },
  { t: "유리와 금속의 초현실적 액체 조각, 스튜디오 조명", en: "Surreal liquid sculpture of glass and metal, studio lighting", tags: ["3D", "프로덕트"], r: "1 / 1" },
  { t: "수채화 질감의 꽃다발, 따뜻한 아침 햇살", en: "Watercolor-textured bouquet in warm morning light", tags: ["일러스트", "수채화"], r: "3 / 4" },
  { t: "심해를 유영하는 발광 해파리 군집, 청록빛 빛줄기", en: "Glowing jellyfish swarm drifting in the deep sea, teal light rays", tags: ["판타지", "자연"], r: "1 / 1" },
  { t: "복고풍 80년대 그래픽, 그리드 지평선과 석양", en: "Retro 80s graphics, grid horizon and sunset", tags: ["레트로", "신스웨이브"], r: "4 / 3" },
  { t: "눈 덮인 북유럽 마을의 밤, 따뜻한 창문 불빛", en: "Snow-covered Nordic village at night, warm window glow", tags: ["풍경", "겨울"], r: "1 / 1" },
  { t: "기하학적 종이접기 학, 부드러운 그림자와 단색 배경", en: "Geometric origami crane, soft shadows on a monochrome backdrop", tags: ["미니멀", "오브제"], r: "1 / 1" },
  { t: "사막 한가운데 떠 있는 초현실 거대 행성", en: "Surreal giant planet floating over the desert", tags: ["초현실", "SF"], r: "3 / 4" },
  { t: "마카롱 색감의 클레이 3D 캐릭터, 귀여운 표정", en: "Macaron-colored clay 3D character with a cute expression", tags: ["3D", "캐릭터"], r: "1 / 1" },
  { t: "도시 야경을 담은 더블 익스포저 인물 실루엣", en: "Double-exposure portrait silhouette holding a city night view", tags: ["인물", "더블노출"], r: "3 / 4" },
  { t: "이끼 낀 고대 신전을 휘감은 빛나는 덩굴", en: "Glowing vines wrapping a moss-covered ancient temple", tags: ["판타지", "자연"], r: "4 / 3" },
  { t: "투명한 유리 질감의 추상 타이포그래피", en: "Abstract typography with a translucent glass texture", tags: ["타이포", "3D"], r: "1 / 1" },
  { t: "벚꽃이 흩날리는 교토 골목의 봄 풍경", en: "Spring in a Kyoto alley with falling cherry blossoms", tags: ["풍경", "봄"], r: "1 / 1" },
  { t: "우주 정거장 창밖으로 보이는 푸른 지구", en: "Blue Earth seen from a space station window", tags: ["SF", "우주"], r: "4 / 3" },
  { t: "리소그래프 질감의 산악 등반 포스터", en: "Risograph-textured mountaineering poster", tags: ["포스터", "리소"], r: "3 / 4" },
  { t: "물 위에 번지는 잉크의 추상 매크로 촬영", en: "Abstract macro shot of ink blooming in water", tags: ["추상", "매크로"], r: "1 / 1" },
  { t: "황금빛 들판을 가르는 외딴 시골길, 일몰", en: "A lone country road through golden fields at sunset", tags: ["풍경", "시네마틱"], r: "4 / 3" },
  { t: "홀로그램 질감의 미래 운동화 프로덕트 샷", en: "Holographic futuristic sneaker product shot", tags: ["프로덕트", "3D"], r: "1 / 1" },
  { t: "별이 쏟아지는 사막 캠프파이어의 밤", en: "Desert campfire night under a sky full of stars", tags: ["풍경", "밤"], r: "3 / 4" },
  { t: "수묵화 스타일의 호랑이, 여백의 미", en: "Ink-wash style tiger, the beauty of negative space", tags: ["일러스트", "동양화"], r: "1 / 1" },
  { t: "버블검 색감의 추상 액체 메탈 풍경", en: "Bubblegum-colored abstract liquid-metal landscape", tags: ["추상", "3D"], r: "4 / 3" },
  { t: "빈티지 식물도감 일러스트, 세밀한 펜 드로잉", en: "Vintage botanical illustration, detailed pen drawing", tags: ["일러스트", "빈티지"], r: "3 / 4" },
];

function buildGallery() {
  return PROMPTS.map((p, i) => {
    const rnd = mulberry32(1000 + i * 97);
    const author = AUTHORS[i % AUTHORS.length];
    const palette = PALETTE_KEYS[i % PALETTE_KEYS.length];
    return {
      id: "img-" + (i + 1),
      seed: 100 + i * 13,
      palette,
      prompt: p.t,
      promptEn: p.en,
      tags: p.tags,
      ratio: p.r,
      model: MODELS[i % MODELS.length],
      author,
      likes: 40 + Math.floor(rnd() * 1800),
      views: 600 + Math.floor(rnd() * 24000),
      steps: [28, 30, 35, 40][i % 4],
      cfg: [4.5, 6, 7, 7.5][i % 4],
      ar: ["3:4", "1:1", "4:3", "16:9"][i % 4],
      createdDays: 1 + Math.floor(rnd() * 28),
    };
  });
}

const GALLERY = buildGallery();
const ALL_TAGS = Array.from(new Set(GALLERY.flatMap((g) => g.tags)));

const STYLE_PRESETS = [
  { id: "none",    label: "스타일 없음", labelEn: "No style", palette: "mono" },
  { id: "cinema",  label: "시네마틱",   labelEn: "Cinematic", palette: "dusk" },
  { id: "anime",   label: "애니메이션", labelEn: "Anime", palette: "candy" },
  { id: "3d",      label: "3D 렌더",    labelEn: "3D Render", palette: "ocean" },
  { id: "water",   label: "수채화",     labelEn: "Watercolor", palette: "bloom" },
  { id: "neon",    label: "네온",       labelEn: "Neon", palette: "candy" },
  { id: "mini",    label: "미니멀",     labelEn: "Minimal", palette: "sand" },
  { id: "film",    label: "필름 사진",  labelEn: "Film", palette: "ember" },
];

const AR_PRESETS = [
  { id: "1:1",  label: "1:1",  ratio: "1 / 1" },
  { id: "3:4",  label: "3:4",  ratio: "3 / 4" },
  { id: "4:3",  label: "4:3",  ratio: "4 / 3" },
  { id: "16:9", label: "16:9", ratio: "16 / 9" },
  { id: "9:16", label: "9:16", ratio: "9 / 16" },
];

Object.assign(window, {
  ArtImage, artBackground, ART_PALETTES, PALETTE_KEYS,
  GALLERY, ALL_TAGS, AUTHORS, MODELS, STYLE_PRESETS, AR_PRESETS, mulberry32,
});
